/**
 * The shapes of everything the ledger knows about. These are plain objects so they can be
 * saved to IndexedDB, sent as JSON, and snapshotted without any conversion.
 */
import type { Cents } from "./money.ts";

/** A calendar date as "YYYY-MM-DD". */
export type IsoDate = string;
/** A local date-time as "YYYY-MM-DDTHH:MM" (payments keep the time they were logged). */
export type IsoDateTime = string;

export interface Person {
  id: string;
  name: string;
  /** One of the palette names, e.g. "Red". Used for the tick boxes and charts. */
  color: string;
  archived: boolean;
}

export interface Store {
  name: string;
  /** Default tax for this store in basis points (7.75% = 775). Older presets hold tax + tip here. */
  taxBp: number;
  /** Default tip for this store (restaurants), on the pre-tax amount. Absent = 0. */
  tipBp?: number;
  /** Default category for this store's items. */
  category: string;
  /** Merchant category code (4 digits), used for card rewards. */
  mcc?: string;
}

/** The fields of an item a person can edit. */
export interface ItemFields {
  date: IsoDate;
  store: string;
  itemNo: string;
  name: string;
  /** null = use the store's default category. */
  category: string | null;
  priceCents: Cents;
  /** Coupons are zero or negative. */
  couponCents: Cents;
  /** California redemption value (bottle deposit). */
  crvCents: Cents;
  /** null = use the store's default tax. (Rows imported from the Sheet hold tax + tip here.) */
  taxBp: number | null;
  /** Tip on the pre-tax amount; null/absent = the store's default tip. */
  tipBp?: number | null;
  /** Which card or method paid the store. */
  paidWith: string | null;
  /** Person id when a friend paid the store instead of you (they get credit). */
  paidBy: string | null;
  /** "Nick 2, Carrie 1" (weights) or "Nick $25" (dollars). Empty = even split. */
  customSplit: string;
  note: string;
  /**
   * Which receipt this was on, when one store visit had more than one ("" = the only or main
   * one). Only a label to show: the receipt itself is `receiptId` when there is one.
   */
  receipt?: string;
  /**
   * The receipt this item is on, by id (Miles, 2026-10-28: ids, not names). Items with the same
   * id are one receipt whatever their store, date or label say, so renaming any of those never
   * moves an item off its receipt, loses its printed total or unlocks it. Absent on items from
   * before ids: those are grouped by date + store + label (tripKey) until they get one.
   */
  receiptId?: string;
  /**
   * The Custom split by person id, worked out from `customSplit` when it's saved (Miles,
   * 2026-10-28). The math uses this, never the names in the text, so renaming someone never
   * changes who pays what. Absent (items from before ids): the text is read by name.
   */
  splitBy?: SplitBy | null;
}

/** A custom split by person id: weights (x1000) or fixed amounts (cents), like the text says. */
export interface SplitBy {
  mode: "weights" | "dollars";
  values: Record<string, number>;
  /** Problems with the text when it was saved ("There's nobody called Zed."), to show. */
  errors?: string[];
}

export interface Item extends ItemFields {
  id: string;
  /** Person ids ticked on this item, in the order they were ticked. */
  ticked: string[];
}

export interface Payment {
  id: string;
  at: IsoDateTime;
  personId: string;
  /** Positive = they paid you. Negative = you paid them. */
  cents: Cents;
  note: string;
}

export interface Settings {
  ownerPersonId: string | null;
  overdueWarnDays: number;
  overdueLateDays: number;
  categories: string[];
  cards: string[];
  /** What members (friends with accounts) may see in this group; absent = the defaults. */
  memberAccess?: MemberAccess;
  /** What a club treasurer (runs this one group) may see; absent = everything. */
  treasurerAccess?: TreasurerAccess;
  /**
   * Changes for one person on top of their role's pages (keyed by person id): pages turned off
   * or on just for them, and categories whose items they don't see. Absent = the role's setting.
   */
  personAccess?: Record<string, PersonAccess>;
  /**
   * Who can join from the sign-up page: "open" = anyone who says they're in it joins right away
   * as a new person, "ask" = you confirm first, "closed" = invite links only. Absent = "open".
   * Saying you're someone already in the group always needs your OK.
   */
  joining?: Joining;
  /**
   * Receipts locked (true) or opened (false) by hand, keyed by receiptKey. A receipt not listed is
   * open when dated on or after RECEIPTS_OPEN_FROM (see receiptLock.ts). Absent = none set.
   */
  receiptLocks?: Record<string, boolean>;
  /** Set once the receipts already there were locked by name: new receipts start open. */
  receiptsOpenByDefault?: boolean;
}

export type Joining = "open" | "ask" | "closed";

/** The owner's sandbox group ("Tests"): receipt test bench lives here, and nobody else joins it. */
export const isTestsGroup = (name: string) => /^tests?$/i.test(name.trim());

export function joiningOf(settings: Pick<Settings, "joining">, groupName = ""): Joining {
  // The Tests group is only ever yours.
  if (isTestsGroup(groupName)) return "closed";
  return settings.joining ?? "open";
}

/**
 * Pages a member can open in a group, besides the Ledger (always). Everyone sees the same group;
 * this only decides which extra pages show.
 */
export interface MemberAccess {
  /** Everyone in the group and what each owes. Default on. */
  people?: boolean;
  /** The Receipts page. Default on. */
  receipts?: boolean;
  /** Charts of the group's spending. Default on. */
  insights?: boolean;
}

export const MEMBER_ACCESS_DEFAULTS: Required<MemberAccess> = {
  people: true,
  receipts: true,
  insights: true,
};

export function memberAccessOf(settings: Pick<Settings, "memberAccess">): Required<MemberAccess> {
  return { ...MEMBER_ACCESS_DEFAULTS, ...(settings.memberAccess ?? {}) };
}

/** Pages a club treasurer can open, besides the Ledger (always). Default: all of them. */
export interface TreasurerAccess {
  people?: boolean;
  receipts?: boolean;
  insights?: boolean;
  history?: boolean;
  settings?: boolean;
}

export const TREASURER_ACCESS_DEFAULTS: Required<TreasurerAccess> = {
  people: true,
  receipts: true,
  insights: true,
  history: true,
  settings: true,
};

export function treasurerAccessOf(
  settings: Pick<Settings, "treasurerAccess">,
): Required<TreasurerAccess> {
  return { ...TREASURER_ACCESS_DEFAULTS, ...(settings.treasurerAccess ?? {}) };
}

/** One person's own page and category settings in a group (see Settings.personAccess). */
export interface PersonAccess {
  /** Pages shown (true) or hidden (false) just for this person; absent = their role's setting. */
  pages?: TreasurerAccess;
  /** Categories whose items this person doesn't see. Items they're on always show. */
  hiddenCategories?: string[];
}

const pick = <T extends object>(pages: Partial<Record<keyof T, boolean>> | undefined, keys: T) =>
  Object.fromEntries(
    Object.entries(pages ?? {}).filter(([k, v]) => k in keys && typeof v === "boolean"),
  ) as Partial<T>;

/** A member's pages in a group: the group's member setting, then their own changes. */
export function memberAccessFor(
  settings: Pick<Settings, "memberAccess" | "personAccess">,
  personId: string,
): Required<MemberAccess> {
  const own = settings.personAccess?.[personId]?.pages;
  return { ...memberAccessOf(settings), ...pick(own, MEMBER_ACCESS_DEFAULTS) };
}

/** A club treasurer's pages in their group: the group's treasurer setting, then their own. */
export function treasurerAccessFor(
  settings: Pick<Settings, "treasurerAccess" | "personAccess">,
  personId: string | null,
): Required<TreasurerAccess> {
  const own = personId ? settings.personAccess?.[personId]?.pages : undefined;
  return { ...treasurerAccessOf(settings), ...pick(own, TREASURER_ACCESS_DEFAULTS) };
}

/** Categories hidden from one person in a group. */
export function hiddenCategoriesFor(
  settings: Pick<Settings, "personAccess">,
  personId: string,
): string[] {
  return settings.personAccess?.[personId]?.hiddenCategories ?? [];
}

/** What you know about a product, shared by every purchase of it (keyed by catalogKey). */
export interface CatalogNote {
  /** Other words for it ("arizona", "iced tea"); search finds items by these too. */
  keywords: string[];
  /** An emoji shown next to the item; absent = picked from the name automatically. */
  icon?: string;
  /** Whether it's usually taxed; null/absent = not known. */
  taxable?: boolean | null;
  note?: string;
  /** A current price you saw (cents), for price adjustments, and the day you saw it. */
  priceNowCents?: number;
  priceSeenOn?: IsoDate;
  /** Who set it: you, or the local AI model's lookup. */
  source?: "you" | "ai";
}

/** The catalog key for an item: its item number when it has one, else its name. */
export function catalogKey(item: { itemNo: string; name: string }): string {
  return item.itemNo.trim() ? `#${item.itemNo.trim()}` : `n:${item.name.trim().toLowerCase()}`;
}

export interface GroupState {
  groupId: string;
  name: string;
  settings: Settings;
  people: Record<string, Person>;
  personOrder: string[];
  stores: Record<string, Store>;
  items: Record<string, Item>;
  /** Item ids in the order they were added (the Sheet's row order for imports). */
  itemOrder: string[];
  payments: Record<string, Payment>;
  paymentOrder: string[];
  /** Printed receipt totals, keyed by receiptKey (a receipt id, or date|store|label before ids). */
  receipts: Record<string, Cents>;
  /** Keywords, icons and tax notes per product (absent in states saved before v0.11). */
  catalogNotes?: Record<string, CatalogNote>;
}

export const PALETTE = [
  "Red",
  "Orange",
  "Yellow",
  "Green",
  "Blue",
  "Purple",
  "Pink",
  "Teal",
  "Rose",
  "Peach",
  "Sky",
  "Lavender",
  "Sage",
  "Gray",
  "Sand",
  "Mauve",
] as const;

/** One receipt: a date, a store and (when a visit had several) the receipt's label. */
export function tripKey(date: IsoDate, store: string, receipt = ""): string {
  return receipt ? `${date}|${store}|${receipt}` : `${date}|${store}`;
}

/** A receipt id as a key ("r:<id>"), next to the older date|store|label keys. */
export const RECEIPT_ID_PREFIX = "r:";

/**
 * The key of the receipt an item is on: its receipt id when it has one, else date + store +
 * label. Use this (never tripKey on an item) for grouping, printed totals and locks.
 */
export function receiptKey(r: {
  date: IsoDate;
  store: string;
  receipt?: string;
  receiptId?: string;
}): string {
  return r.receiptId
    ? `${RECEIPT_ID_PREFIX}${r.receiptId}`
    : tripKey(r.date, r.store, r.receipt ?? "");
}

export function emptyState(groupId: string): GroupState {
  return {
    groupId,
    name: "",
    settings: {
      ownerPersonId: null,
      overdueWarnDays: 7,
      overdueLateDays: 14,
      categories: [],
      cards: [],
    },
    people: {},
    personOrder: [],
    stores: {},
    items: {},
    itemOrder: [],
    payments: {},
    paymentOrder: [],
    receipts: {},
    catalogNotes: {},
  };
}
